import {formatDistanceToNowStrict} from "date-fns";
import {useMemo} from "react";
import {FiExternalLink} from "react-icons/fi";
import {Link} from "react-router";

import {Card} from "@/components/card/Card.tsx";
import {Icon} from "@/components/icon/Icon.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {StandardDateRange} from "@/lib/queries/reports.ts";
import {StandardDateRangeLabels} from "@/lib/types/reports.ts";
import {DashboardChartWidget} from "@/lib/types/website.ts";
import {ChartEmptyState} from "@/pages/metricsExplorer/charts/ChartEmptyState.tsx";
import {EXPLORER_CHART_TYPES, ExplorerChartType} from "@/pages/metricsExplorer/explorerConfig.ts";
import {buildExplorerViewLink} from "@/pages/metricsExplorer/explorerViewLink.ts";
import {MetricsExplorerChart} from "@/pages/metricsExplorer/MetricsExplorerChart.tsx";

import styles from "./DashboardChartCard.module.scss";

interface DashboardChartCardProps {
  widget: DashboardChartWidget;
}

const chartTypeOf = (value: string | null): ExplorerChartType =>
  EXPLORER_CHART_TYPES.includes(value as ExplorerChartType) ? (value as ExplorerChartType) : "line";

/** One saved chart on the dashboard, read-only, drawn over the window the server charted. */
export const DashboardChartCard = ({widget}: DashboardChartCardProps) => {
  const checkNames = useMemo(
    () => Object.fromEntries(widget.check_names.map((check) => [check.check_id, check.name])),
    [widget.check_names]
  );

  const rangeLabel = StandardDateRangeLabels[StandardDateRange[widget.time_range]];
  const result = widget.result;
  // Opening the chart needs it to still exist. The server leaves the name unset exactly
  // when the saved chart has been deleted, which is the case it reports as
  // VIEW_UNAVAILABLE, so there is nothing to link to then.
  const explorerLink = widget.view_name ? buildExplorerViewLink(widget.view_id, widget.time_range) : null;

  return (
    <Card variant="white" className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{widget.view_name ?? "Chart unavailable"}</span>
        <div className={styles.headerMeta}>
          <span className={styles.meta}>
            {rangeLabel}
            {widget.computed_at && ` · updated ${formatDistanceToNowStrict(new Date(widget.computed_at))} ago`}
          </span>
          {explorerLink && (
            <Link
              to={explorerLink}
              className={styles.explorerLink}
              aria-label="Open in Metrics Explorer"
              title="Open in Metrics Explorer"
            >
              <Icon icon={FiExternalLink} display="inline" removeDivDimensions />
            </Link>
          )}
        </div>
      </div>
      {widget.status === "ERROR" ? (
        <ChartEmptyState
          title="This chart cannot be shown"
          message={widget.error_message ?? "The chart could not be loaded."}
        />
      ) : result && widget.start_date && widget.end_date ? (
        // The server's window, not one computed here: a stale chart may trail the present by a
        // refresh, and its axis has to match the data it actually holds.
        <MetricsExplorerChart
          series={result.series}
          analysis={result.analysis}
          chartType={chartTypeOf(widget.chart_type)}
          checkNames={checkNames}
          granularity={widget.granularity ?? "AUTO"}
          start={new Date(widget.start_date)}
          end={new Date(widget.end_date)}
        />
      ) : (
        <LoadingContainer text="Preparing chart…" />
      )}
    </Card>
  );
};
